import { Component, Input, OnChanges, OnDestroy } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { IntakeFormSection, IntakeOption } from '@ee/common/models';
import { IntakeFormFields } from '@ee/common/enums';
import { setControlAsked } from './unasked-control';

@Component({
  selector: 'ee-lease-terms-section',
  template: `
    <div class="section-wrapper" [formGroup]="form">
      <div class="in-row-grid">
        @if (showField(options[intakeFields.LEASE_RENT_AMOUNT])) {
          <mat-form-field
            class="flex-1 md:flex-none"
            [ngClass]="{ 'hidden-from-intake': !!options[intakeFields.LEASE_RENT_AMOUNT]?.hide_from_intake }">
            <mat-label>Lease Monthly Rent</mat-label>
            <span matPrefix>$ &nbsp;</span>
            <input
              matInput
              type="number"
              formControlName="lease_rent_amount" />
            @if (form?.get('lease_rent_amount').hasError('required')) {
              <mat-error>Please enter a value.</mat-error>
            }
            @if (form?.get('lease_rent_amount').hasError('pattern')) {
              <mat-error>Invalid currency format.</mat-error>
            }
            <mat-hint>
              {{ options[intakeFields.LEASE_RENT_AMOUNT].required ? 'Required' : 'Optional' }}
              @if (leaseTermsSection?.localization?.['lease_rent_amount_hint']) {
                <span class="field-instructions-wrapper">
                  . Hover for
                  <span
                    class="field-instructions"
                    [tp]="leaseTermsSection?.localization?.['lease_rent_amount_hint']"
                    tpPlacement="bottom">
                    more info
                  </span>
                  .
                </span>
              }
            </mat-hint>
          </mat-form-field>
        }
        @if (showField(options[intakeFields.LEASE_RENT_DUE_DAY])) {
          <mat-form-field
            class="flex-1 md:flex-none"
            [ngClass]="{ 'hidden-from-intake': !!options[intakeFields.LEASE_RENT_DUE_DAY]?.hide_from_intake }">
            <mat-label>Due Day</mat-label>
            <input
              matInput
              type="text"
              formControlName="lease_rent_due_day" />
            @if (form?.get('lease_rent_due_day').hasError('required')) {
              <mat-error>Please enter a value.</mat-error>
            }
            <mat-hint>
              {{ options[intakeFields.LEASE_RENT_DUE_DAY].required ? 'Required' : 'Optional' }}
              @if (leaseTermsSection?.localization?.['lease_rent_due_day_hint']) {
                <span class="field-instructions-wrapper">
                  . Hover for
                  <span
                    class="field-instructions"
                    [tp]="leaseTermsSection?.localization?.['lease_rent_due_day_hint']"
                    tpPlacement="bottom">
                    more info
                  </span>
                  .
                </span>
              }
            </mat-hint>
          </mat-form-field>
        }
        @if (showField(options[intakeFields.LEASE_RENT_FREQUENCY])) {
          <mat-form-field
            class="flex-1 md:flex-none"
            [ngClass]="{ 'hidden-from-intake': !!options[intakeFields.LEASE_RENT_FREQUENCY]?.hide_from_intake }">
            <mat-label>Rent Frequency</mat-label>
            <input
              matInput
              type="text"
              formControlName="lease_rent_frequency" />
            @if (form?.get('lease_rent_frequency').hasError('required')) {
              <mat-error>Please enter a value.</mat-error>
            }
            <mat-hint>
              {{ options[intakeFields.LEASE_RENT_FREQUENCY].required ? 'Required' : 'Optional' }}
              @if (leaseTermsSection?.localization?.['lease_rent_frequency_hint']) {
                <span class="field-instructions-wrapper">
                  . Hover for
                  <span
                    class="field-instructions"
                    [tp]="leaseTermsSection?.localization?.['lease_rent_frequency_hint']"
                    tpPlacement="bottom">
                    more info
                  </span>
                  .
                </span>
              }
            </mat-hint>
          </mat-form-field>
        }
        @if (showField(options[intakeFields.SECURITY_DEPOSIT])) {
          <mat-form-field
            class="flex-1 md:flex-none"
            [ngClass]="{ 'hidden-from-intake': !!options[intakeFields.SECURITY_DEPOSIT]?.hide_from_intake }">
            <mat-label>Security Deposit</mat-label>
            <span matPrefix>$ &nbsp;</span>
            <input
              matInput
              type="number"
              formControlName="security_deposit" />
            @if (form?.get('security_deposit').hasError('required')) {
              <mat-error>Please enter a value.</mat-error>
            }
            @if (form?.get('security_deposit').hasError('pattern')) {
              <mat-error>Invalid currency format.</mat-error>
            }
            <mat-hint>
              {{ options[intakeFields.SECURITY_DEPOSIT].required ? 'Required' : 'Optional' }}
              @if (leaseTermsSection?.localization?.['security_deposit_hint']) {
                <span class="field-instructions-wrapper">
                  . Hover for
                  <span
                    class="field-instructions"
                    [tp]="leaseTermsSection?.localization?.['security_deposit_hint']"
                    tpPlacement="bottom">
                    more info
                  </span>
                  .
                </span>
              }
            </mat-hint>
          </mat-form-field>
        }
        @if (showField(options[intakeFields.LEASE_TERMS])) {
          <mat-form-field
            class="flex-1 md:flex-none"
            [ngClass]="{ 'hidden-from-intake': !!options[intakeFields.LEASE_TERMS]?.hide_from_intake }">
            <mat-label>Lease Terms</mat-label>
            <mat-select formControlName="lease_terms">
              @for (term of leaseTerms; track term) {
                <mat-option [value]="term">
                  {{ term }}
                </mat-option>
              }
            </mat-select>
            @if (form?.get('lease_terms')?.hasError('required')) {
              <mat-error>Please enter a value.</mat-error>
            }
            <mat-hint>
              {{ options[intakeFields.LEASE_TERMS].required ? 'Required' : 'Optional' }}
              @if (leaseTermsSection?.localization?.['lease_terms_hint']) {
                <span class="field-instructions-wrapper">
                  . Hover for
                  <span class="field-instructions" [tp]="leaseTermsSection?.localization?.['lease_terms_hint']" tpPlacement="bottom">
                    more info
                  </span>
                  .
                </span>
              }
            </mat-hint>
          </mat-form-field>
        }
        @if (showField(options[intakeFields.MOVE_IN_DATE])) {
          <mat-form-field
            class="flex-1 md:flex-none"
            [ngClass]="{ 'hidden-from-intake': !!options[intakeFields.MOVE_IN_DATE]?.hide_from_intake }">
            <mat-label>Move-In Date</mat-label>
            <input
              matInput
              [matDatepicker]="moveInDate"
              formControlName="move_in_date" />
            <mat-datepicker-toggle matSuffix [for]="moveInDate"></mat-datepicker-toggle>
            <mat-datepicker #moveInDate></mat-datepicker>
            @if (form?.get('move_in_date').hasError('matDatepickerParse')) {
              <mat-error>Enter a date as MM/DD/YYYY.</mat-error>
            } @else if (form?.get('move_in_date').hasError('required')) {
              <mat-error>Please enter a value.</mat-error>
            }
            <mat-hint>
              {{ options[intakeFields.MOVE_IN_DATE].required ? 'Required' : 'Optional' }}
              @if (leaseTermsSection?.localization?.['move_in_date_hint']) {
                <span class="field-instructions-wrapper">
                  . Hover for
                  <span class="field-instructions" [tp]="leaseTermsSection?.localization?.['move_in_date_hint']" tpPlacement="bottom">
                    more info
                  </span>
                  .
                </span>
              }
            </mat-hint>
          </mat-form-field>
        }
      </div>

      @if (showField(options[intakeFields.WAIVER_OF_NOTICE]) || showField(options[intakeFields.NOTICE_DATE])) {
        <div
          class="in-row-grid"
          [ngClass]="{ 'hidden-from-intake': !!options[intakeFields.NOTICE_DATE]?.hide_from_intake }">
          @if (showField(options[intakeFields.WAIVER_OF_NOTICE])) {
            <div>
              <mat-checkbox formControlName="waiver_of_notice">Lease contains a waiver of notice</mat-checkbox>
            </div>
          }
          @if (
            showField(options[intakeFields.NOTICE_DATE]) &&
            ((!form.get(intakeFields.WAIVER_OF_NOTICE)?.value && form.get(intakeFields.NOTICE_DATE)) ||
              overrideFieldVisibility)
          ) {
            <mat-form-field>
              <mat-label>Notice Date</mat-label>
              <input
                matInput
                [matDatepicker]="noticedp"
                formControlName="notice_date" />
              <mat-datepicker-toggle matSuffix [for]="noticedp"></mat-datepicker-toggle>
              <mat-datepicker #noticedp></mat-datepicker>
              @if (form.get(intakeFields.NOTICE_DATE).hasError('matDatepickerParse')) {
                <mat-error>Enter a date as MM/DD/YYYY.</mat-error>
              } @else if (form.get(intakeFields.NOTICE_DATE).hasError('required')) {
                <mat-error>Please select a notice date.</mat-error>
              }
              <mat-hint>
                {{ options[intakeFields.NOTICE_DATE].required ? 'Required' : 'Optional' }}
                @if (leaseTermsSection?.localization?.['notice_date_hint']) {
                  <span class="field-instructions-wrapper">
                    . Hover for
                    <span class="field-instructions" [tp]="leaseTermsSection?.localization?.['notice_date_hint']" tpPlacement="bottom">
                      more info
                    </span>
                    .
                  </span>
                }
              </mat-hint>
            </mat-form-field>
          }
        </div>
      }
      @if (leaseTermsSection?.fields?.length && leaseTermsFields) {
        <div class="in-row-grid">
          @for (f of leaseTermsSection?.fields; track f; let i = $index) {
            @if (!f.hide_from_intake || overrideFieldVisibility) {
              <ee-field
                [compact]="false"
                [ngClass]="{
                  'margin-bottom-sm': i < leaseTermsSection.fields.length - 1,
                  'md:mb-4': i < leaseTermsSection.fields.length - 1,
                  'double-column': f.columns === 2,
                  'triple-column': f.columns === 3,
                  'quad-column': f.columns === 4
                }"
                [hideFromIntake]="f.hide_from_intake"
                [control]="$any(leaseTermsFields.controls[f.field])"
                [field]="f"></ee-field>
            }
          }
        </div>
      }
    </div>
  `,
  standalone: false
})
export class LeaseTermsSectionComponent implements OnChanges, OnDestroy {
  @Input() demoMode = false;

  @Input() overrideFieldVisibility = false;

  @Input() form: UntypedFormGroup;

  @Input() options: Map<string, IntakeOption> | undefined;

  @Input() leaseTermsSection: IntakeFormSection | undefined;

  intakeFields = IntakeFormFields;

  year = new Date().getFullYear();

  subs: Subscription[] = [];

  /** Built-ins the lease step can ask. `notice_date` follows its own row/waiver conditions. */
  private static readonly LEASE_FIELDS: IntakeFormFields[] = [
    IntakeFormFields.LEASE_RENT_AMOUNT,
    IntakeFormFields.LEASE_RENT_DUE_DAY,
    IntakeFormFields.LEASE_RENT_FREQUENCY,
    IntakeFormFields.SECURITY_DEPOSIT,
    IntakeFormFields.LEASE_TERMS,
    IntakeFormFields.MOVE_IN_DATE,
    IntakeFormFields.WAIVER_OF_NOTICE,
    IntakeFormFields.NOTICE_DATE
  ];

  /** Re-syncs Notice Date when the waiver checkbox flips; replaced on every input change. */
  private waiverSub?: Subscription;

  ngOnChanges(): void {
    this.syncUnaskedLeaseControls();
    this.waiverSub?.unsubscribe();
    this.waiverSub = this.form
      ?.get(this.intakeFields.WAIVER_OF_NOTICE)
      ?.valueChanges.subscribe(() => this.syncUnaskedLeaseControls());
  }

  ngOnDestroy(): void {
    this.waiverSub?.unsubscribe();
    this.subs.forEach((x) => x.unsubscribe());
  }

  /** Matches the template: the notice row, then the waiver-off (or override) condition on the input. */
  private noticeDateIsRendered(): boolean {
    const notice = this.options[this.intakeFields.NOTICE_DATE];
    const rowShown = this.showField(this.options[this.intakeFields.WAIVER_OF_NOTICE]) || this.showField(notice);
    const waived = !!this.form.get(this.intakeFields.WAIVER_OF_NOTICE)?.value;
    return rowShown && this.showField(notice) && (!waived || this.overrideFieldVisibility);
  }

  /**
   * Suspend validators on every lease control the client cannot see (option off or hide-from-intake;
   * see setControlAsked), so a required field that is not on screen cannot block Next — the empty
   * LEASE TERMS card in ISS-178 — while a cloned/edited case keeps its stored value. Notice Date
   * follows the template's row and waiver conditions. Custom fields need nothing here: a
   * hide-from-intake custom field is never built with a required validator.
   */
  private syncUnaskedLeaseControls(): void {
    if (!this.form || !this.options) {
      return;
    }

    LeaseTermsSectionComponent.LEASE_FIELDS.forEach((field) => {
      const control = this.form.get(field);
      if (control) {
        const asked = field === this.intakeFields.NOTICE_DATE ? this.noticeDateIsRendered() : this.showField(this.options[field]);
        setControlAsked(control, asked);
      }
    });
  }

  get leaseTermsFields(): UntypedFormGroup | undefined {
    return this.form.get('custom_fields') as UntypedFormGroup;
  }

  showField(option: IntakeOption): boolean {
    return option?.show && (!option.hide_from_intake || this.demoMode || this.overrideFieldVisibility);
  }

  public get leaseTerms(): string[] {
    let options = ['Month to Month', 'Written Lease'];

    if (this.options && this.options[this.intakeFields.LEASE_TERMS]?.options?.length) {
      options = this.options[this.intakeFields.LEASE_TERMS]?.options;
    }

    return options;
  }
}
